import { useProfileCompetencesStore } from './profileCompetencesStore'
/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { ITab } from '@/src/interfaces/ITab'
import {
  ProfileSectionEnum,
  type PublicProfile,
  type PublishJobProfileSection,
  type UpdateProfileRequest,
  type CandidateProfile,
  type CvSection,
  type NetworkPublishProfileSection,
  type NewProfileSection,
  type ProfileData
} from '../../../../generated-api'
import {
  useUserStore,
  useAppStore,
  useDocumentsStore,
  useDrawerStore,
  useNextJobStore,
  useProfileCVStore
} from '@/stores'
import { factory } from '../../../../factory-api/factory'
import { createBadge, populateDrawerList } from '@/application/helpers/profileHelper'
import type { IDrawerList } from '@/interfaces/components/IDrawerList'
import type { IProfileError } from '@/interfaces/IProfileError'
import { openUrl } from '@/application/helpers'
import { useProfileExperienceStore } from './profileExperienceStore'

export const useProfileStore = defineStore('profileStore', () => {
  const appStore = useAppStore()
  const documentsStore = useDocumentsStore()
  const userStore = useUserStore()
  const drawerStore = useDrawerStore()
  const profileExperienceStore = useProfileExperienceStore()
  const profileCompetencesStore = useProfileCompetencesStore()
  const profileNextJobStore = useNextJobStore()
  const profileCVStore = useProfileCVStore()

  const initialized = ref<boolean>(false)
  const profile = ref<PublicProfile>()
  const tabs = ref<ITab[]>([
    {
      id: 1,
      name: '',
      href: '#',
      icon: 'file-pen',
      current: false
    },
    {
      id: 2,
      name: '',
      href: '#',
      icon: 'file-user',
      current: false
    }
  ])
  const activeTab = ref<ITab>(tabs.value[0])
  const profileImage = ref<string>('')
  const isDownloadingCV = ref<boolean>(false)
  const profileDrawerList = ref<IDrawerList[]>([])
  const networkSection = ref<NetworkPublishProfileSection>()
  const networkSectionPublished = ref<boolean>(false)
  const jobProfileSection = ref<PublishJobProfileSection>()
  const jobProfileSectionPublished = ref<boolean>(false)
  const profileDrawer = ref<any | undefined>(undefined)
  const profileData = ref<ProfileData>()
  const cvSection = ref<CvSection>()

  // Actions
  const init = async () => {
    profileData.value = appStore.content.profileData

    // Your initialization logic
    tabs.value[0].name = profileData.value?.tabNameEditProfile ?? ''
    tabs.value[1].name = profileData.value?.tabNameViewProfile ?? ''

    // Get public cv document
    cvSection.value = profileData.value?.sections?.find(
      (section: NewProfileSection) => section.sectionType === 'cv'
    )

    documentsStore.setLocalPublicDocument(cvSection.value?.publicCVDocument)

    networkSection.value = profileData.value?.networkPublishProfileSection
    networkSectionPublished.value = networkSection.value?.isPublished ?? false

    jobProfileSection.value = appStore.content.profileData.jobProfilePublishSection
    jobProfileSectionPublished.value = appStore.content.profileData.jobProfilePublished

    mapDrawerList()

    initialized.value = true
  }

  const setActiveTab = (tab: ITab) => {
    activeTab.value = tab
  }

  const getCandidatePublicProfile = async () => {
    try {
      const memberApi = await factory.getMemberApi()
      const response = await memberApi.getCandidatePublicProfiles({
        userId: userStore.member?.id ?? ''
      })

      profile.value = response.publicJobProfile
    } catch (error) {
      console.log(error)
    }
  }

  const getProfileImage = async () => {
    try {
      const fileApi = await factory.getFileApi()
      const response = await fileApi.candidateImageBase64({
        candidateId: userStore.member?.id ?? ''
      })
      profileImage.value = response != '' ? 'data:image/jpeg;base64,' + response : ''
    } catch (error) {
      console.log(error)
    }
  }

  const saveCandiateProfile = async (
    candidateProfile: CandidateProfile,
    type: ProfileSectionEnum
  ) => {
    const profileRequest: UpdateProfileRequest = {
      onlyPersonalData: false,
      profileSection: type,
      candidateProfile: candidateProfile
    }

    const api = await factory.getMemberApi()
    const response = await api.updateProfile(profileRequest)

    if (type === ProfileSectionEnum.Experience && response?.experience != undefined) {
      profileExperienceStore.setExperiences(response?.experience)
    }
  }

  const downloadCV = async (documentId?: string, programmeId?: string) => {
    isDownloadingCV.value = true

    const publicCvDocument = documentsStore.publicDocument
    const proId = publicCvDocument?.programmeId ?? ''

    try {
      await documentsStore.downloadDocument(
        programmeId != undefined ? programmeId : proId,
        documentId != undefined ? documentId : (publicCvDocument?.id ?? '')
      )
      isDownloadingCV.value = false
    } catch (error) {
      console.log(error)
      isDownloadingCV.value = false
    }
  }

  const mapDrawerList = () => {
    profileDrawerList.value = populateDrawerList()
  }

  const setNetworkSection = (section: NetworkPublishProfileSection) => {
    networkSection.value = section
  }

  const setNetworkSectionPublished = (published: boolean) => {
    networkSectionPublished.value = published
  }

  const setJobProfileSection = (section: PublishJobProfileSection) => {
    jobProfileSection.value = section
  }

  const setJobProfileSectionPublished = (published: boolean) => {
    jobProfileSectionPublished.value = published
  }

  const getProfileDrawer = (drawerType: string) => {
    profileDrawer.value = profileDrawerList.value
      .flatMap((section) => section.list) // Flatten all items into one array
      .find((item) => item.type === drawerType)
  }

  const setBadgeOnDrawerList = (
    drawerType: string,
    condition: boolean,
    activeText: string,
    inactiveText: string
  ) => {
    getProfileDrawer(drawerType)

    profileDrawer.value.badge = createBadge(condition, activeText, inactiveText)
  }

  const profileRedirect = (error: IProfileError) => {
    if (error.drawer != undefined && error.drawer !== '') {
      replaceDrawerContent(error.drawer)
    } else {
      openUrl(error.link, false, '_self')
    }
  }

  const replaceDrawerContent = (drawerType: string) => {
    getProfileDrawer(drawerType)

    drawerStore.setActiveDrawer(profileDrawer.value)
  }

  const setProfileImage = (source: string) => {
    profileImage.value = source
  }

  const resetProfileStores = () => {
    profileExperienceStore.setExperienceInitialized(false)

    profileCompetencesStore.setInitialized(false)

    profileNextJobStore.setIsInitialized(false)

    profileCVStore.setInitialized(false)
  }

  return {
    profile,
    initialized,
    tabs,
    activeTab,
    profileImage,
    isDownloadingCV,
    downloadCV,
    init,
    setActiveTab,
    saveCandiateProfile,
    getCandidatePublicProfile,
    getProfileImage,
    profileDrawerList,
    networkSection,
    setNetworkSection,
    setNetworkSectionPublished,
    networkSectionPublished,
    profileDrawer,
    getProfileDrawer,
    setBadgeOnDrawerList,
    jobProfileSection,
    jobProfileSectionPublished,
    setJobProfileSection,
    setJobProfileSectionPublished,
    profileRedirect,
    cvSection,
    profileData,
    setProfileImage,
    resetProfileStores
  }
})
